import React, { useState } from "react";
import { Camera, Upload, Loader2, X, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const MAX_BYTES = 5 * 1024 * 1024;

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_BYTES) return reject(new Error("Image is too large (max 5MB)"));
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const MAX = 1600;
        const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error("Couldn't read image"));
    img.src = url;
  });
}

/**
 * One-tap "Submit a photo for this campsite" button + modal. No Trip Wall
 * required — just upload, optional caption, submit. Lands in the same admin
 * moderation queue as Trip Wall opt-in photos. Designed for non-technical
 * users who want to contribute without thinking too hard.
 */
export default function SubmitCampsitePhotoButton({ campsiteId, campsiteName, className = "" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [photo, setPhoto] = useState(null); // { data_url, name }
  const [caption, setCaption] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onTriggerClick = () => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`/campsites/${campsiteId}?upload=1`)}`);
      return;
    }
    setOpen(true);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data_url = await fileToDataUrl(file);
      setPhoto({ data_url, name: file.name });
    } catch (err) {
      toast.error(err.message || "Couldn't load image");
    }
  };

  const submit = async () => {
    if (!photo) return toast.error("Pick a photo first");
    setSubmitting(true);
    try {
      await api.post("/photos/submit", {
        campsite_id: campsiteId,
        image_data_url: photo.data_url,
        caption: caption.trim() || null,
      });
      toast.success("Thanks — a moderator will review your photo shortly.");
      setOpen(false);
      setPhoto(null);
      setCaption("");
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <button
        onClick={onTriggerClick}
        className={`inline-flex items-center gap-2 text-xs mono uppercase tracking-wider px-3 py-2 border-2 hairline-strong hover:bg-[#fff4ee] hover:border-[#ff4500] hover:text-[#ff4500] transition-colors ${className}`}
        data-testid="submit-photo-trigger"
      >
        <Camera className="w-3.5 h-3.5" strokeWidth={2} />
        Submit a photo
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          data-testid="submit-photo-modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.target === e.currentTarget && !submitting && setOpen(false)}
        >
          <div className="bg-white w-full sm:max-w-md max-h-[95vh] overflow-y-auto border-2 hairline-strong shadow-[0_8px_0_0_#111]">
            <header className="px-5 py-3 border-b hairline flex items-center justify-between">
              <h2 className="font-display text-lg leading-tight">Photo for {campsiteName}</h2>
              <button
                onClick={() => !submitting && setOpen(false)}
                className="p-1 hover:bg-[#f5f5f5]"
                aria-label="Close"
                data-testid="submit-photo-close"
              >
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </header>

            <div className="px-5 py-4 space-y-4">
              <div className="text-xs leading-snug text-[#555] flex items-start gap-2 bg-[#fff4ee] p-3 border hairline">
                <AlertCircle className="w-4 h-4 text-[#ff4500] flex-shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>
                  <strong>Landscapes &amp; surroundings only.</strong> Please don't include people. Our team reviews each photo before it appears on the campsite page.
                </span>
              </div>

              {photo ? (
                <div className="relative">
                  <img src={photo.data_url} alt={photo.name} className="w-full max-h-72 object-contain bg-[#f5f5f5] border hairline" />
                  <button
                    onClick={() => setPhoto(null)}
                    className="absolute top-2 right-2 bg-black/70 text-white p-1.5 hover:bg-black"
                    aria-label="Remove photo"
                    data-testid="submit-photo-remove"
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </button>
                </div>
              ) : (
                <label className="block border-2 border-dashed hairline-strong p-8 text-center cursor-pointer hover:bg-[#fff4ee]" data-testid="submit-photo-pick">
                  <Upload className="w-7 h-7 text-[#ff4500] mx-auto mb-2" strokeWidth={1.75} />
                  <div className="text-sm font-display leading-tight">Tap to choose a photo</div>
                  <div className="text-[11px] mono text-[#888] mt-1">JPG / PNG / HEIC · max 5MB</div>
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                </label>
              )}

              <div>
                <label className="eyebrow block mb-1.5">Caption (optional)</label>
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value.slice(0, 160))}
                  placeholder="View from pitch 12 at sunset"
                  className="w-full border hairline px-3 py-2 text-sm"
                  data-testid="submit-photo-caption"
                />
                <div className="text-[10px] mono text-[#888] text-right mt-1">{caption.length}/160</div>
              </div>
            </div>

            <footer className="px-5 py-3 border-t hairline flex items-center justify-between gap-3">
              <button
                onClick={() => !submitting && setOpen(false)}
                className="text-sm mono underline text-[#666]"
                disabled={submitting}
                data-testid="submit-photo-cancel"
              >Cancel</button>
              <button
                onClick={submit}
                disabled={submitting || !photo}
                className="btn-primary text-xs px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="submit-photo-submit"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" strokeWidth={2} />}
                Submit photo
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
